import { useState } from 'react';
import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { COMMENT_BODY_MAX, type AssetComment, type AssetDetail } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { formatDateTime } from './format';
import { ApiError } from '../gallery/api';
import { Portal } from '../components/Portal';
import { ConfirmModal } from '../taxonomy/ConfirmModal';
import { createComment, deleteComment, updateComment } from './viewer-api';

/**
 * Éditeur de COMMENTAIRES de la visionneuse (LOT 2). Fil ACTIF vivant dans la barre
 * supérieure de la visionneuse. ÉPURÉ : plus de titre, plus d'état vide, plus de
 * champ « Type » (un commentaire n'est qu'un CORPS texte côté UI ; le type reste
 * une chaîne en base, normalisée à `''` par le backend).
 *
 * - Liste les commentaires existants (`detail.comments`), chacun ÉDITABLE (corps
 *   → `PATCH`) et SUPPRIMABLE (`DELETE`, confirmation via NOTRE modale).
 * - Ajout : textarea CORPS multi-lignes → `POST`.
 * - Écritures OPTIMISTES remontées via `onDetailChange` ; REVERT + message sur
 *   échec (403 = lecture seule → message dédié).
 *
 * En LECTURE SEULE (`readOnly`, bénéficiaire de lien d'accès `user`), on MASQUE toutes
 * les affordances d'écriture (ajout, édition, suppression) : le fil reste VISIBLE en
 * consultation (corps + auteur + date), sans aucun champ de saisie ni bouton d'action.
 */
interface CommentsEditorProps {
  detail: AssetDetail;
  onDetailChange: (next: AssetDetail) => void;
  /** Lecture seule : fil de commentaires en CONSULTATION, sans contrôles d'édition. */
  readOnly?: boolean;
}

export function CommentsEditor({
  detail,
  onDetailChange,
  readOnly = false,
}: CommentsEditorProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const [bodyDraft, setBodyDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<'generic' | 'readonly' | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  // Suppression : id du commentaire ciblé par la confirmation (NOTRE modale, jamais
  // `window.confirm`). `null` = aucune confirmation ouverte.
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const replaceComments = (comments: AssetComment[]): void => {
    onDetailChange({ ...detail, comments });
  };

  const setErrorFrom = (err: unknown): void => {
    setError(err instanceof ApiError && err.status === 403 ? 'readonly' : 'generic');
  };

  const submitAdd = async (): Promise<void> => {
    const body = bodyDraft.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      const comment = await createComment(detail.id, body);
      replaceComments([...detail.comments, comment]);
      setBodyDraft('');
    } catch (err) {
      setErrorFrom(err);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (comment: AssetComment): void => {
    setEditingId(comment.id);
    setEditBody(comment.body);
  };

  const cancelEdit = (): void => {
    setEditingId(null);
    setEditBody('');
  };

  const submitEdit = async (id: string): Promise<void> => {
    const body = editBody.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateComment(detail.id, id, body);
      replaceComments(detail.comments.map((c) => (c.id === id ? updated : c)));
      cancelEdit();
    } catch (err) {
      setErrorFrom(err);
    } finally {
      setBusy(false);
    }
  };

  // Effacement RÉEL, après confirmation via NOTRE modale : optimiste (retire tout de
  // suite, remet en cas d'échec).
  const performDelete = async (id: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setDeleteTarget(null);
    const previous = detail.comments;
    replaceComments(detail.comments.filter((c) => c.id !== id));
    try {
      await deleteComment(detail.id, id);
    } catch (err) {
      replaceComments(previous);
      setErrorFrom(err);
    } finally {
      setBusy(false);
    }
  };

  // LECTURE SEULE : fil en consultation — corps + auteur/date, aucun contrôle d'édition,
  // aucun champ d'ajout. (Les commentaires EXISTANTS restent visibles pour le lecteur.)
  if (readOnly) {
    return (
      <section className="flex flex-col gap-2">
        {/* Aucun commentaire (audit F10) : en lecture seule il n'y a AUCUN autre
            contenu — sans ce message, le panneau s'ouvrait totalement muet. */}
        {detail.comments.length === 0 && (
          <p className="text-sm text-white/70">{t('comments.empty')}</p>
        )}
        <ul className="flex flex-col gap-2">
          {detail.comments.map((comment) => (
            <li key={comment.id} className="rounded-md bg-white/10 p-2">
              <div className="flex flex-col gap-1">
                <p className="whitespace-pre-wrap break-words text-sm text-white">{comment.body}</p>
                <span className="text-xs text-white/70">
                  {comment.authorName} · {formatDateTime(comment.createdAt, locale)}
                  {comment.updatedAt !== comment.createdAt ? ` (${t('comments.edited')})` : ''}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-2">
      {detail.comments.length === 0 && (
        <p className="text-sm text-white/70">{t('comments.empty')}</p>
      )}
      <ul className="flex flex-col gap-2">
        {detail.comments.map((comment) => (
          <li key={comment.id} className="rounded-md bg-white/10 p-2">
            {editingId === comment.id ? (
              <div className="flex flex-col gap-2">
                <textarea
                  value={editBody}
                  maxLength={COMMENT_BODY_MAX}
                  onChange={(e) => setEditBody(e.target.value)}
                  aria-label={t('comments.bodyLabel')}
                  rows={3}
                  className="w-full rounded-md border border-white/20 bg-black/30 p-2 text-sm text-white placeholder:text-white/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="rounded-md px-2 py-1 text-xs text-white/80 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                  >
                    {t('comments.cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={busy || editBody.trim().length === 0}
                    onClick={() => void submitEdit(comment.id)}
                    className="rounded-md bg-brand px-2 py-1 text-xs font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    {busy ? t('comments.saving') : t('comments.save')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <p className="whitespace-pre-wrap break-words text-sm text-white">{comment.body}</p>
                <div className="flex items-center justify-between gap-2 text-xs text-white/70">
                  <span>
                    {comment.authorName} · {formatDateTime(comment.createdAt, locale)}
                    {comment.updatedAt !== comment.createdAt ? ` (${t('comments.edited')})` : ''}
                  </span>
                  <span className="flex gap-1">
                    <button
                      type="button"
                      aria-label={t('comments.edit')}
                      title={t('comments.edit')}
                      onClick={() => startEdit(comment)}
                      className="rounded p-1 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                    >
                      <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      aria-label={t('comments.delete')}
                      title={t('comments.delete')}
                      onClick={() => setDeleteTarget(comment.id)}
                      className="rounded p-1 hover:text-danger focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </span>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* Ajout d'un commentaire. */}
      <div className="flex flex-col gap-2">
        <textarea
          value={bodyDraft}
          maxLength={COMMENT_BODY_MAX}
          onChange={(e) => setBodyDraft(e.target.value)}
          aria-label={t('comments.bodyLabel')}
          placeholder={t('comments.bodyPlaceholder')}
          rows={2}
          className="w-full rounded-md border border-white/20 bg-black/30 p-2 text-sm text-white placeholder:text-white/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        />
        <div className="flex items-center justify-between gap-2">
          {error !== null ? (
            <span role="alert" className="text-xs text-danger">
              {error === 'readonly' ? t('comments.errorReadonly') : t('comments.error')}
            </span>
          ) : (
            <span />
          )}
          <button
            type="button"
            disabled={busy || bodyDraft.trim().length === 0}
            onClick={() => void submitAdd()}
            className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            <span>{busy ? t('comments.saving') : t('comments.add')}</span>
          </button>
        </div>
      </div>

      {/* Confirmation de suppression (NOTRE modale, jamais `window.confirm`) — portée
          hors de l'en-tête `backdrop-blur`. Action danger : icône + texte. */}
      {deleteTarget !== null && (
        <Portal>
          <ConfirmModal
            title={t('comments.confirmTitle')}
            confirmLabel={t('comments.confirmAction')}
            cancelLabel={t('comments.cancel')}
            busyLabel={t('comments.deleting')}
            busy={busy}
            danger
            onConfirm={() => void performDelete(deleteTarget)}
            onClose={() => setDeleteTarget(null)}
          >
            <p>{t('comments.confirmDelete')}</p>
          </ConfirmModal>
        </Portal>
      )}
    </section>
  );
}
